import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Page, PageHeader } from "@/components/PageHeader";
import { Crown, PencilSimple } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const NONE = "__none__";

export default function SoysosStays() {
  const { data: stays = [] } = useQuery({ queryKey: ["sos-stays"], queryFn: async () => (await api.get("/soysos/stays")).data });
  return (
    <Page>
      <PageHeader title="In-House Guests" subtitle="Only checked-in guests can raise requests. Room + last name verifies identity." />
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <table className="w-full text-sm" data-testid="sos-stays-table">
          <thead className="bg-secondary/50 text-muted-foreground text-[12px] uppercase tracking-wide">
            <tr>
              <th className="text-left font-semibold px-4 py-3">Room</th>
              <th className="text-left font-semibold px-4 py-3">Guest</th>
              <th className="text-left font-semibold px-4 py-3">VIP</th>
              <th className="text-left font-semibold px-4 py-3">Check-out</th>
              <th className="text-right font-semibold px-4 py-3">Edit</th>
            </tr>
          </thead>
          <tbody>
            {stays.map((s) => (
              <tr key={s.id} className="border-t border-border" data-testid={`sos-stay-${s.room_number}`}>
                <td className="px-4 py-3 font-medium">{s.room_number}</td>
                <td className="px-4 py-3">{s.guest_name}</td>
                <td className="px-4 py-3">{s.vip_tier ? <span className="inline-flex items-center gap-1 text-[12px] text-amber-600 capitalize"><Crown size={13} weight="fill" /> {s.vip_tier}</span> : <span className="text-muted-foreground">—</span>}</td>
                <td className="px-4 py-3 text-muted-foreground">{s.checkout ? new Date(s.checkout).toLocaleDateString() : "—"}</td>
                <td className="px-4 py-3 text-right"><EditStayDialog stay={s} /></td>
              </tr>
            ))}
            {!stays.length && <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">No in-house guests.</td></tr>}
          </tbody>
        </table>
      </div>
    </Page>
  );
}

function EditStayDialog({ stay }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [room, setRoom] = useState(stay.room_number || "");
  const [firstName, setFirstName] = useState(stay.first_name || "");
  const [lastName, setLastName] = useState(stay.last_name || "");
  const [phone, setPhone] = useState(stay.phone || "");
  const [vip, setVip] = useState(stay.vip_tier || NONE);

  const reset = () => {
    setRoom(stay.room_number || ""); setFirstName(stay.first_name || "");
    setLastName(stay.last_name || ""); setPhone(stay.phone || ""); setVip(stay.vip_tier || NONE);
  };

  const mut = useMutation({
    mutationFn: async () => (await api.patch(`/soysos/stays/${stay.id}`, {
      room_number: room.trim(), first_name: firstName.trim(), last_name: lastName.trim(),
      phone: phone.trim(), vip_tier: vip === NONE ? "" : vip,
    })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["sos-stays"] }); toast.success("Guest updated"); setOpen(false); },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Update failed"),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) reset(); }}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="gap-1.5" data-testid={`edit-stay-btn-${stay.room_number}`}>
          <PencilSimple size={14} /> Edit
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="edit-stay-dialog">
        <DialogHeader><DialogTitle>Edit in-house guest</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs mb-1 block">Room number</Label>
              <Input value={room} onChange={(e) => setRoom(e.target.value)} data-testid="edit-stay-room" />
            </div>
            <div>
              <Label className="text-xs mb-1 block">Phone</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="edit-stay-phone" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs mb-1 block">First name</Label>
              <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} data-testid="edit-stay-first" />
            </div>
            <div>
              <Label className="text-xs mb-1 block">Last name</Label>
              <Input value={lastName} onChange={(e) => setLastName(e.target.value)} data-testid="edit-stay-last" />
            </div>
          </div>
          <div>
            <Label className="text-xs mb-1 block">VIP tier</Label>
            <Select value={vip} onValueChange={setVip}>
              <SelectTrigger data-testid="edit-stay-vip"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>None</SelectItem>
                <SelectItem value="silver">Silver</SelectItem>
                <SelectItem value="gold">Gold</SelectItem>
                <SelectItem value="platinum">Platinum</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button disabled={!room.trim() || !lastName.trim() || mut.isPending} onClick={() => mut.mutate()} data-testid="edit-stay-save-btn">
            {mut.isPending ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
